import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api, ApiClientError } from '@/lib/api.js';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'manager' | 'developer' | 'team_member' | 'viewer';
  avatar_url?: string | null;
  timezone?: string;
  is_active?: boolean;
}

export interface LoginResponse {
  user?: User;
  otp_required?: boolean;
  challenge_token?: string;
  email?: string;
  expires_in?: number;
  debug_otp?: string;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResponse>;
  verifyOtp: (challengeToken: string, otp: string, rememberMe?: boolean) => Promise<User>;
  resendOtp: (challengeToken: string) => Promise<{ success: boolean; message?: string; debug_otp?: string }>;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
}

const AUTH_STORAGE_KEY = 'tsos_active_user_session';
const SESSION_STORAGE_KEY = 'tsos_session_user_session';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize from sessionStorage or localStorage so reloads never lose active session
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      const sessionSaved = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (sessionSaved) return JSON.parse(sessionSaved);
      const localSaved = localStorage.getItem(AUTH_STORAGE_KEY);
      return localSaved ? JSON.parse(localSaved) : null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState(!user);
  const [error, setError] = useState<string | null>(null);

  const refetchUser = useCallback(async () => {
    try {
      if (!user) setIsLoading(true);
      setError(null);
      const res = await api.get<{ data: User }>('/api/v1/auth/me');
      if (res?.data) {
        setUser(res.data);
        try {
          const isSessionOnly = !!sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (isSessionOnly) {
            sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(res.data));
          } else {
            localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(res.data));
          }
        } catch {}
      }
    } catch (err: any) {
      if (err instanceof ApiClientError && err.status === 401) {
        // Clear session if explicitly unauthorized (session expired or invalidated)
        const hadActiveSession = !!user || (typeof window !== 'undefined' && !!(sessionStorage.getItem(SESSION_STORAGE_KEY) || localStorage.getItem(AUTH_STORAGE_KEY)));
        try {
          localStorage.removeItem(AUTH_STORAGE_KEY);
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        } catch {}
        setUser(null);

        if (hadActiveSession && typeof window !== 'undefined') {
          const currentHash = window.location.hash.replace(/^#/, '');
          if (currentHash && !currentHash.startsWith('/login')) {
            try {
              sessionStorage.setItem('tsos_pre_login_path', currentHash);
            } catch {}
          }
          if (!window.location.hash.includes('/login')) {
            window.location.hash = '#/login?expired=true';
          }
        }
      } else {
        console.warn('Auth check fallback:', err.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refetchUser();
  }, [refetchUser]);

  const login = async (email: string, password: string, rememberMe: boolean = true): Promise<LoginResponse> => {
    setError(null);
    const res = await api.post<{ data: LoginResponse }>('/api/v1/auth/login', {
      email,
      password,
      remember_me: rememberMe,
    });
    const data = res?.data || (res as any) || {};
    if (data.otp_required) {
      return {
        otp_required: true,
        challenge_token: data.challenge_token,
        email: data.email,
        expires_in: data.expires_in || 300,
        debug_otp: data.debug_otp,
        message: data.message,
      };
    }
    if (data.user) {
      setUser(data.user);
      try {
        if (rememberMe) {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data.user));
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        } else {
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data.user));
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } catch {}
      return { user: data.user, otp_required: false };
    }
    return data;
  };

  const verifyOtp = async (challengeToken: string, otp: string, rememberMe: boolean = true): Promise<User> => {
    setError(null);
    const res = await api.post<{ data: { user: User } }>('/api/v1/auth/verify-otp', {
      challenge_token: challengeToken,
      otp,
      remember_me: rememberMe,
    });
    const verifiedUser = res?.data?.user;
    if (verifiedUser) {
      setUser(verifiedUser);
      try {
        if (rememberMe) {
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(verifiedUser));
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        } else {
          sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(verifiedUser));
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } catch {}
      return verifiedUser;
    }
    throw new Error('Verification failed. No user returned.');
  };

  const resendOtp = async (challengeToken: string) => {
    const res = await api.post<{ data: { success: boolean; message: string; debug_otp?: string } }>('/api/v1/auth/resend-otp', {
      challenge_token: challengeToken,
    });
    return res?.data || (res as any);
  };

  const logout = async () => {
    try {
      await api.post('/api/v1/auth/logout');
    } catch {
      // Logout even if request fails
    } finally {
      setUser(null);
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      } catch {}
    }
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, error, login, verifyOtp, resendOtp, logout, refetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
